import { CheckmarkIc, RemoveIc, HelpIc } from '@dsch/dd-icons';

import { Button } from '../../../../Toolkit/Button';
import { Link } from '../../../../Toolkit/Button/Link';
import { InfoTitle } from '../../../../Toolkit/InfoTitle/InfoTitle';
import { InfoListItem } from '../../../../Toolkit/InfoListItem/InfoListItem';
import Text from '../../../../ToolkitV2/Text/Text';
import { CDN_STATIC_ASSETS } from '../../../../../utils';

import type { FreeHistoryCheckPanelProps } from './FreeHistoryCheckPanel.typed';
import * as Styled from './FreeHistoryCheckPanel.styled';
import { useTheme } from 'styled-components';
import { WriteOffModalInfoIcon } from '../WriteOffModal/WriteOffModalInfoIcon';

const FreeHistoryCheckPanel = (props: FreeHistoryCheckPanelProps) => {
  const {
    greenlightVerified,
    greenlightV2,
    openFreeHistoryCheckModal,
    displayTrustedDealerPanel = false,
  } = props;
  const theme = useTheme();

  const { titleGroup, infoLink, listBlock, reportInfo } = greenlightV2;

  const displayNonVerifiedMessage = !greenlightVerified && listBlock?.extraInfo;

  const NON_VERIFIED_MESSAGE = listBlock?.extraInfo?.text;

  const IconMap: { [key: string]: JSX.Element } = {
    'icon-check-positive-tick': (
      <CheckmarkIc width="16" height="16" color={theme.donedeal.colors.GREEN} />
    ),
    'icon-check-neutral-dash': (
      <RemoveIc
        width="16"
        height="16"
        data-testid="positive-remove-icon"
        color={theme.donedeal.colors.GREY_DARK}
      />
    ),
    'icon-check-question-mark': (
      <HelpIc
        width="16"
        height="16"
        data-testid="question-mark-icon"
        color={theme.donedeal.colors.GREY_DARK}
      />
    ),
  };

  return (
    <Styled.FreeHistoryCheckPanelContainer
      displayTrustedDealerPanel={displayTrustedDealerPanel}
      data-testid="free-history-check-panel"
    >
      <Styled.FreeBadge>
        <Text size="B12" color="WHITE" weight="bold" forwardedAs="span">
          FREE
        </Text>
      </Styled.FreeBadge>

      <Styled.Header>
        <InfoTitle
          title={titleGroup.title}
          description={
            <Styled.Description>
              <Text size="M16" forwardedAs="span">
                {titleGroup.subtitle}
              </Text>{' '}
              <Link
                fontSize="B14"
                ofType={'SECONDARY'}
                underline
                href={infoLink?.url}
                target="_blank"
                rel="noopener noreferrer"
              >
                {infoLink?.text}
              </Link>
            </Styled.Description>
          }
        />
        <Styled.Icon
          src={`${CDN_STATIC_ASSETS}/images/illustrations/greenlight-green.svg`}
          width="40"
          height="40"
          alt="Greenlight verified"
        />
      </Styled.Header>

      {!displayNonVerifiedMessage && listBlock?.items && (
        <Styled.ListContainer>
          {listBlock.items.map((item) => (
            <InfoListItem
              key={item.title}
              icon={item.icon && IconMap[item.icon]}
              trailingIcon={
                item.id === 'writtenOff' && <WriteOffModalInfoIcon />
              }
              title={item.title}
              variant="MEDIUM"
              alignItems="center"
            />
          ))}
        </Styled.ListContainer>
      )}

      {displayNonVerifiedMessage && (
        <Styled.NonVerifiedMessage>
          {NON_VERIFIED_MESSAGE}
        </Styled.NonVerifiedMessage>
      )}

      {reportInfo && (
        <Button onClick={openFreeHistoryCheckModal} ofType="SECONDARY">
          {reportInfo.buttonText}
        </Button>
      )}
    </Styled.FreeHistoryCheckPanelContainer>
  );
};

export { FreeHistoryCheckPanel };
